import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, X, Send, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useCompanyInfo } from "@/lib/company";

const VISITOR_KEY = "he_chatbot_visitor_id";
const SESSION_KEY = "he_chatbot_session_id";

function getVisitorId() {
  try {
    let v = window.localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = "vis-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch (_e) {
    return "vis-anon";
  }
}

function getSessionId() {
  try { return window.localStorage.getItem(SESSION_KEY) || ""; } catch (_e) { return ""; }
}

function setSessionId(id) {
  try { window.localStorage.setItem(SESSION_KEY, id); } catch (_e) { /* ignore */ }
}

function buildWelcomeMessage(t, pathname) {
  if (pathname.startsWith("/simulateur")) return t("chatbot.welcome_simulator");
  if (pathname.startsWith("/catalogue")) return t("chatbot.welcome_catalogue");
  return t("chatbot.welcome_landing");
}

export default function ChatBotFloat() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const company = useCompanyInfo();
  const enabled = !!company?.chatbot_enabled;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestWa, setSuggestWa] = useState(false);
  const scrollRef = useRef(null);
  const visitorId = useMemo(getVisitorId, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: buildWelcomeMessage(t, location.pathname) }]);
    }
  }, [open, messages.length, t, location.pathname]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Hide on admin routes and when disabled.
  if (!enabled || location.pathname.startsWith("/admin")) return null;

  const send = async (ev) => {
    ev?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    try {
      const locale = (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];
      const { data } = await api.post("/chat/message", {
        session_id: getSessionId() || undefined,
        visitor_id: visitorId,
        message: text,
        locale,
        source_page: location.pathname,
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages([...nextMessages, { role: "assistant", content: data.reply }]);
      setSuggestWa(!!data.suggest_wa);
    } catch (err) {
      const code = err?.response?.status;
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content: code === 429 ? t("chatbot.rate_limited") : t("chatbot.error"),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const waNumber = company?.whatsapp_business_number;
  const waHref = suggestWa && waNumber
    ? `https://wa.me/${waNumber.replace(/[^+\d]/g, "").replace(/^\+/, "")}?text=${encodeURIComponent(t("chatbot.wa_prefill"))}`
    : null;

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="chatbot-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-50 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 transition-transform hover:-translate-y-0.5 hover:bg-emerald-600"
          aria-label={t("chatbot.open")}
        >
          <MessageSquare className="h-6 w-6" strokeWidth={2.2} />
        </button>
      )}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 left-6 z-50 flex h-[560px] w-[380px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-center justify-between bg-emerald-500 px-4 py-3 text-white">
            <div>
              <div className="text-sm font-semibold">{t("chatbot.header_title", { brand: company?.brand_name || "SolarSim" })}</div>
              <div className="text-[11px] text-emerald-50/80">{t("chatbot.header_subtitle")}</div>
            </div>
            <button
              type="button"
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              className="grid h-8 w-8 place-items-center rounded-full text-white/90 hover:bg-white/10"
              aria-label={t("chatbot.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-emerald-500 text-white"
                    : "mr-auto bg-white border border-slate-200 text-solar-slate"
                }`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="mr-auto flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> {t("chatbot.typing")}
              </div>
            )}
            {waHref && !loading && (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="chatbot-wa-cta"
                className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-500 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-500/20"
              >
                {t("chatbot.talk_wa")} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <form
            onSubmit={send}
            className="flex items-center gap-2 border-t border-slate-200 bg-white p-2"
            data-testid="chatbot-form"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chatbot.input_placeholder")}
              data-testid="chatbot-input"
              disabled={loading}
              maxLength={2000}
              className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              data-testid="chatbot-send"
              className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white disabled:bg-emerald-300"
              aria-label={t("chatbot.send")}
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
